import React from 'react';
import { Network, Shield, Zap, Settings } from 'lucide-react';

const SecureSDWAN = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative h-[500px] flex items-center justify-center text-white"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://img1.wsimg.com/isteam/ip/a27d2dc3-82db-4815-b271-215705ee1c2c/secure%20sd%20wan.jpg/:/cr=t:0%25,l:0%25,w:100%25,h:100%25)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">IndyaNet Secure SD-WAN</h1>
          <p className="text-xl md:text-2xl text-gray-200">
            Industry-leading SD-WAN: Accelerating Networks with Scale, Simplicity and Security
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-100 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=1600&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">SD-WAN Key Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Settings className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Centralized Control</h3>
              <p className="text-gray-600 text-sm">
                Managed through a centralized controller or dashboard. Administrators can easily define policies, push
                configurations, and monitor performance.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Network className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Multiple Link Types</h3>
              <p className="text-gray-600 text-sm">
                Supports MPLS, broadband, LTE/5G, and satellite links. Automatically selects the best path based on
                real-time conditions.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Zap className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Dynamic Path Selection</h3>
              <p className="text-gray-600 text-sm">
                Routes traffic based on application needs, latency, jitter, and packet loss. Ensures optimal
                performance for critical applications.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Shield className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Application-Aware Routing</h3>
              <p className="text-gray-600 text-sm">
                Intelligent routing based on application requirements ensuring critical business applications get
                priority treatment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">Architecture</h2>
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-2xl font-semibold mb-6 text-center text-red-600">
              SD-WAN ARCHITECTURE | Multi Cloud | Multi Region | HUB – SPOKE | FULL MESH
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="border-l-4 border-red-600 pl-4">
                <h4 className="font-semibold text-lg mb-2">Multi Cloud</h4>
                <p className="text-gray-600 text-sm">
                  Seamless connectivity across multiple cloud providers (AWS, Azure, GCP) with optimized routing and
                  security.
                </p>
              </div>
              <div className="border-l-4 border-red-600 pl-4">
                <h4 className="font-semibold text-lg mb-2">Multi Region</h4>
                <p className="text-gray-600 text-sm">
                  Connect branches and data centers across different geographical regions with consistent performance.
                </p>
              </div>
              <div className="border-l-4 border-red-600 pl-4">
                <h4 className="font-semibold text-lg mb-2">HUB-SPOKE</h4>
                <p className="text-gray-600 text-sm">
                  Traditional topology where all branch offices connect to a central hub for centralized management
                  and security.
                </p>
              </div>
              <div className="border-l-4 border-red-600 pl-4">
                <h4 className="font-semibold text-lg mb-2">FULL MESH</h4>
                <p className="text-gray-600 text-sm">
                  Direct connectivity between all sites enabling optimal application performance and reduced latency.
                </p>
              </div>
              <div className="border-l-4 border-red-600 pl-4">
                <h4 className="font-semibold text-lg mb-2">Scalability</h4>
                <p className="text-gray-600 text-sm">
                  Easily add new sites, scale bandwidth, and expand network infrastructure as your business grows.
                </p>
              </div>
              <div className="border-l-4 border-red-600 pl-4">
                <h4 className="font-semibold text-lg mb-2">Security</h4>
                <p className="text-gray-600 text-sm">
                  Built-in firewall, encryption, and threat prevention to ensure secure communication across your
                  network.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Why Choose IndyaNet SD-WAN?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Zap className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Improved Performance</h3>
                <p className="text-gray-600">
                  Optimize application performance with intelligent traffic routing and real-time path selection.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Shield className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Enhanced Security</h3>
                <p className="text-gray-600">
                  Built-in security features including encryption, firewall, and threat prevention at every location.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Network className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Cost Reduction</h3>
                <p className="text-gray-600">
                  Reduce dependency on expensive MPLS circuits by leveraging broadband and LTE connections.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Settings className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Simplified Management</h3>
                <p className="text-gray-600">
                  Centralized dashboard for easy configuration, monitoring, and troubleshooting across all sites.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SecureSDWAN;